import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { dashboardAPI, sedesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  Users, Calendar, CheckCircle, Clock, AlertTriangle, TrendingUp,
  Loader2
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';

const COLORS = ['#002FA7', '#34C759', '#FFCC00', '#FF3B30', '#52525B'];

const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [sedes, setSedes] = useState([]);
  const [selectedSede, setSelectedSede] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSedes();
  }, []);

  useEffect(() => {
    if (selectedSede) {
      loadStats();
    }
  }, [selectedSede]);

  const loadSedes = async () => {
    try {
      const response = await sedesAPI.getAll();
      setSedes(response.data);
      if (response.data.length > 0) {
        setSelectedSede(response.data[0].id);
      }
    } catch (error) {
      console.error('Error loading sedes:', error);
    }
  };

  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await dashboardAPI.getStats(selectedSede);
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const kpis = stats ? [
    { label: 'Total Colaboradores', value: stats.total_users, icon: Users, color: 'text-[#002FA7]', bg: 'bg-blue-50' },
    { label: 'Asignaciones Semana', value: stats.week_assignments, icon: Calendar, color: 'text-[#34C759]', bg: 'bg-green-50' },
    { label: 'Confirmados', value: stats.confirmed_count, icon: CheckCircle, color: 'text-[#34C759]', bg: 'bg-green-50' },
    { label: 'Tasa Cumplimiento', value: `${stats.compliance_rate}%`, icon: TrendingUp, color: 'text-[#002FA7]', bg: 'bg-blue-50' },
    { label: 'Solicitudes Pendientes', value: stats.pending_requests, icon: Clock, color: 'text-[#FFCC00]', bg: 'bg-yellow-50' },
    { label: 'Lista de Espera', value: stats.waitlist_count, icon: AlertTriangle, color: 'text-[#FF3B30]', bg: 'bg-red-50' },
  ] : [];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Dashboard Ejecutivo
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Vista general de ocupación y métricas de asistencia
            </p>
          </div>
          <Select value={selectedSede} onValueChange={setSelectedSede}>
            <SelectTrigger className="w-full sm:w-[200px] rounded-sm" data-testid="sede-selector">
              <SelectValue placeholder="Seleccionar sede" />
            </SelectTrigger>
            <SelectContent>
              {sedes.map((sede) => (
                <SelectItem key={sede.id} value={sede.id}>{sede.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
          </div>
        ) : !stats ? (
          <Card className="border border-slate-200">
            <CardContent className="flex flex-col items-center justify-center h-64 text-center">
              <Calendar className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-500">Selecciona una sede o crea una para ver las estadísticas</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {kpis.map((kpi, index) => (
                <Card key={index} className="border border-slate-200 card-hover" data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}>
                  <CardContent className="p-4">
                    <div className={`w-10 h-10 ${kpi.bg} rounded-sm flex items-center justify-center mb-3`}>
                      <kpi.icon className={`w-5 h-5 ${kpi.color}`} />
                    </div>
                    <p className="text-2xl font-bold">{kpi.value}</p>
                    <p className="text-xs text-slate-500 mt-1">{kpi.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Daily Occupancy Chart */}
              <Card className="border border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-semibold">Ocupación Diaria (Esta Semana)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats.daily_occupancy}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                        <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#fff', 
                            border: '1px solid #E4E4E7',
                            borderRadius: '2px'
                          }}
                        />
                        <Bar dataKey="count" fill="#002FA7" name="Asignados" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="capacity" fill="#E4E4E7" name="Capacidad" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Occupancy Percentage */}
              <Card className="border border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-semibold">% Ocupación por Día</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={stats.daily_occupancy}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                        <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} domain={[0, 100]} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#fff', 
                            border: '1px solid #E4E4E7',
                            borderRadius: '2px'
                          }}
                          formatter={(value) => [`${value}%`, 'Ocupación']}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="occupancy_percent" 
                          stroke="#002FA7" 
                          strokeWidth={2}
                          dot={{ fill: '#002FA7', strokeWidth: 2 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Distribution by Day */}
            {stats.day_distribution && stats.day_distribution.length > 0 && (
              <Card className="border border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-semibold">Distribución Histórica por Día</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={stats.day_distribution.map(d => ({
                            name: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie'][d._id],
                            value: d.count
                          }))}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {stats.day_distribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default DashboardPage;
